import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  Keyboard,
} from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
};

// Mock address suggestions (replace with Google Places API in production)
const mockSuggestions = [
  { address: "2750 Paulina St.", city: "Chicago", state: "IL", postalCode: "60614" },
  { address: "West Chicago Avenue", city: "Chicago", state: "IL", postalCode: "60622" },
  { address: "Chicago Avenue", city: "Oak Park", state: "IL", postalCode: "60302" },
  { address: "Chicago Riverwalk", city: "Chicago", state: "IL", postalCode: "60601" },
  { address: "111 N High St", city: "Columbus", state: "OH", postalCode: "43215" },
];


const SenderAddressStep: React.FC = ({ onValidationChange }: Props) => {
  const { t } = useTranslation();
  // display the field label from the gffpFieldsByCategory (from gffp response)
  const gffpFieldsByCategory = useSessionStore((s: SessionState) => (s as any).gffpFieldsByCategory);
  const senderInfoFields: any[] = (gffpFieldsByCategory?.["Sender Information"]) || [];
  const getFieldLabel = (tag: string): string | undefined => {
    const field = senderInfoFields.find((f: any) => (f.XmlTag ?? f.xmlTag) === tag);
    return field?.FieldLabel ?? field?.fieldLabel;
  };

  const addressPlaceholder = getFieldLabel("senderAddress") || "Address";
  const cityPlaceholder = getFieldLabel("senderCity") || t("SENDER_ADDRESS_CITY_PLACEHOLDER");
  const postalPlaceholder = getFieldLabel("senderZipCode") || t("SENDER_ADDRESS_POSTAL_PLACEHOLDER");
  const statePlaceholder = getFieldLabel("senderState") || t("SENDER_ADDRESS_STATE_PLACEHOLDER");
  const [query, setQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<typeof mockSuggestions>([]);
  const senderAddress = useSessionStore((s: SessionState) => (s as any).senderAddress) || "";
  const senderCity = useSessionStore((s: SessionState) => (s as any).senderCity) || "";
  const senderZipCode = useSessionStore((s: SessionState) => (s as any).senderZipCode) || "";
  const senderState = useSessionStore((s: SessionState) => (s as any).senderState) || "";
  const setSenderAddress = useSessionStore((s: SessionState) => (s as any).setSenderAddress);
  const setSenderCity = useSessionStore((s: SessionState) => (s as any).setSenderCity);
  const setSenderZipCode = useSessionStore((s: SessionState) => (s as any).setSenderZipCode);
  const setSenderState = useSessionStore((s: SessionState) => (s as any).setSenderState);

  const isValid = senderAddress.trim().length > 0 &&
    senderCity.trim().length > 0 &&
    senderZipCode.trim().length > 0 &&
    senderState.trim().length > 0 && senderState.trim().length <= 2;
  
    useEffect(() => {
      if (onValidationChange) {
        onValidationChange(isValid);
      }
    }, [isValid, onValidationChange]);
  

  const handleSearch = (text: string) => {
    setQuery(text);
    if (text.length > 1) {
      const filtered = mockSuggestions.filter((s) =>
        s.address.toLowerCase().includes(text.toLowerCase())
      );
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  // Local query state only controls suggestion list; input values bind to store directly

  const handleSelectSuggestion = (item: typeof mockSuggestions[0]) => {
    try { setSenderAddress?.(item.address); } catch {}
    try { setSenderCity?.(item.city); } catch {}
    try { setSenderZipCode?.(item.postalCode); } catch {}
    try { setSenderState?.(item.state); } catch {}
    setQuery(item.address);
    setShowSuggestions(false);
    Keyboard.dismiss();
  };

  const handleFieildChange = (text: string) => {
    let isContinue = false;
    if (senderAddress?.length > 1 &&
      senderCity?.length > 1 &&
      senderZipCode?.length > 1 &&
      senderState?.length > 1) {
      isContinue = true
    }
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("SENDER_ADDRESS_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("SENDER_ADDRESS_SUBTITLE")}</Text>

      {/* Search Field 
      <TextInput
        style={styles.input}
        placeholder="Search"
        value={query}
        onChangeText={handleSearch}
      />*/}

      {/* Suggestions List 
      {showSuggestions && (
        <FlatList
          data={suggestions}
          keyExtractor={(_, idx) => idx.toString()}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={styles.suggestionItem}
              onPress={() => handleSelectSuggestion(item)}
            >
              <Text style={styles.suggestionText}>
                {item.address}, {item.city}, {item.state}
              </Text>
            </TouchableOpacity>
          )}
        />
      )}*/}

      {/* Manual Entry Fields */}
        
        <View style={styles.form}>
          <TextInput
            style={styles.input}
            placeholder={addressPlaceholder}
            value={senderAddress}
            onChangeText={(val) => { try { setSenderAddress?.(val); } catch {} }}
          />
          
          <TextInput
            style={styles.input}
            placeholder={cityPlaceholder}
            value={senderCity}
            onChangeText={(val) => { try { setSenderCity?.(val); } catch {} }}
          />
          <TextInput
            style={styles.input}
            placeholder={postalPlaceholder}
            value={senderZipCode}
            onChangeText={(val) => { try { setSenderZipCode?.(val); } catch {} }}
          />
          <TextInput
            style={styles.input}
            placeholder={statePlaceholder}
            value={senderState}
            onChangeText={(val) => { try { setSenderState?.(val); } catch {} }}
          />
        </View>
      

      {/* Info Box */}
      <View style={styles.infoBox}>
        <Text style={styles.infoText}>
          {t("YOUR_INFO_IS_ALWAYS_KEPT_PRIVATE")}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    marginTop: 16,
    backgroundColor: "#FFF"
  },
  title: { fontSize: 22, fontWeight: "700", marginBottom: 4 },
  subtitle: { fontSize: 16, color: "#555", marginBottom: 16 },
  input: {
    backgroundColor: "#f5f5f5",
    padding: 12,
    borderRadius: 8,
    fontSize: 16,
    marginBottom: 10,
  },
  suggestionItem: { padding: 12, borderBottomWidth: 1, borderColor: "#eee" },
  suggestionText: { fontSize: 16 },
  form: { marginTop: 16 },
  manualBtn: { marginTop: 16, alignItems: "center" },
  manualText: { fontSize: 16, fontWeight: "600" },
  infoBox: {
    marginTop: 20,
    padding: 12,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 8,
  },
  infoText: { fontSize: 14, color: "#333" },
});

export default SenderAddressStep;